import React, { useState } from "react";

import {
  CheckOutlined,
  LeftOutlined,
  MinusOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { toast } from "react-toastify";

import PdfPreview from "../../Common/PdfPreview";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";

import { getUserCredentials } from "../../../utils/common";

import { previewSubmissionInvoice, saveSubmissionInvoice } from "../../../actions/invoice";

import { ReactComponent as CheckboxCircleFillIcon } from "../../../assets/images/subscriptions/checkbox-circle-fill.svg";
import { ReactComponent as DownloadLineIcon } from "../../../assets/images/subscriptions/download-line.svg";

import "./PreviewInvoice.css";

const STEPS = ["Select submissions", "Set invoice details", "Preview & save"];
const ACTIVE_STEP = 2;
const MIN_ZOOM = 6;
const MAX_ZOOM = 14;

const getStepStatus = (index) => {
  if (index < ACTIVE_STEP) return "complete";
  if (index === ACTIVE_STEP) return "active";
  return "upcoming";
};

export default function PreviewInvoice({ invoice, onBack, onSaved }) {
  const {
    title,
    previewUrl,
    filename,
    previewPayload,
    amount,
    invoiceDate,
    invoiceName,
  } = invoice;
  const [zoom, setZoom] = useState(10);
  const [saving, setSaving] = useState(false);

  const changeZoom = (direction) => {
    setZoom((current) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current + direction)));
  };

  const downloadInvoice = () => {
    const anchor = document.createElement("a");
    anchor.href = previewUrl;
    if (filename) anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const saveInvoice = async () => {
    if (saving) return;
    if (!previewPayload) {
      toast.error("Invoice PDF is not available");
      return;
    }
    const user = getUserCredentials() || {};
    const createdBy = String(user.user_name || user.user_email || "").trim();
    if (!createdBy) {
      toast.error("Unable to save this invoice");
      return;
    }

    setSaving(true);
    const uploaded = await previewSubmissionInvoice({
      ...previewPayload,
      uploadToS3: true,
    });
    if (!uploaded.success || !uploaded.pdfPath) {
      setSaving(false);
      toast.error(uploaded.message || "Failed to save invoice");
      return;
    }

    const response = await saveSubmissionInvoice({
      pdfPath: uploaded.pdfPath,
      amount,
      invoiceDate,
      createdBy,
      clientName: previewPayload.clientName,
      invoiceName,
    });
    setSaving(false);

    if (!response.success) {
      toast.error(response.message || "Failed to save invoice");
      return;
    }

    const invoiceNumber = response.invoice?.invoice_number;
    toast.success(invoiceNumber ? `Invoice ${invoiceNumber} saved` : "Invoice saved");
    onSaved();
  };

  return (
    <div className="PreviewInvoice">
      <header className="PreviewInvoice__Header">
        <div className="PreviewInvoice__TitleBlock">
          <Button
            type="text"
            className="PreviewInvoice__Back"
            onClick={onBack}
            disabled={saving}
            aria-label="Back to edit"
            icon={<LeftOutlined />}
          />
          <div>
            <h1 className="PreviewInvoice__Title">Preview &amp; Save</h1>
            <p className="PreviewInvoice__Description">
              Review the final invoice exactly as it will appear before saving it.
            </p>
          </div>
        </div>
        <ol className="PreviewInvoice__Steps">
          {STEPS.map((label, index) => {
            const status = getStepStatus(index);

            return (
              <li key={label} className={`is-${status}`}>
                <span className="PreviewInvoice__Step">
                  <span
                    className={`PreviewInvoice__StepIcon PreviewInvoice__StepIcon--${status}`}
                    aria-hidden
                  >
                    {status === "complete" ? <CheckboxCircleFillIcon /> : index + 1}
                  </span>
                  <span>{label}</span>
                </span>
                {index < STEPS.length - 1 && (
                  <RightOutlined className="PreviewInvoice__StepChevron" />
                )}
              </li>
            );
          })}
        </ol>
      </header>

      <div className="PreviewInvoice__Canvas">
        <p className="PreviewInvoice__DocTitle">{title}</p>
        <div className="PreviewInvoice__CanvasScrollHost">
          <StyledScrollbar
            className="PreviewInvoice__CanvasScroll"
            noScrollX
            disableTrackYWidthCompensation
            disableTracksWidthCompensation
          >
            <div className="PreviewInvoice__CanvasContent">
              <div
                className="PreviewInvoice__Document"
                style={{ width: zoom === MAX_ZOOM ? "100%" : `min(100%, ${72 * zoom}px)` }}
              >
                <div className="PreviewInvoice__Pdf">
                  <PdfPreview key={zoom} blob={previewUrl} height="auto" scaleNumber={zoom / 10} />
                </div>
              </div>
            </div>
          </StyledScrollbar>
        </div>

        <div className="PreviewInvoice__Zoom" role="group" aria-label="Zoom invoice">
          <Button
            aria-label="Zoom in"
            disabled={zoom >= MAX_ZOOM}
            icon={<PlusOutlined />}
            onClick={() => changeZoom(1)}
          />
          <Button
            aria-label="Zoom out"
            disabled={zoom <= MIN_ZOOM}
            icon={<MinusOutlined />}
            onClick={() => changeZoom(-1)}
          />
        </div>
      </div>

      <footer className="PreviewInvoice__Footer">
        <Button className="PreviewInvoice__BackEdit" onClick={onBack} disabled={saving}>
          Back to edit
        </Button>
        <div className="PreviewInvoice__FooterActions">
          <Button
            className="PreviewInvoice__Download"
            icon={<DownloadLineIcon />}
            iconPosition="end"
            onClick={downloadInvoice}
          >
            Download invoice
          </Button>
          <Button
            type="primary"
            className="PreviewInvoice__Save"
            icon={<CheckOutlined />}
            iconPosition="end"
            loading={saving}
            onClick={saveInvoice}
          >
            Save Invoice
          </Button>
        </div>
      </footer>
    </div>
  );
}
